import type { PollService } from "@busbud/core-types";
import type { CountryCode2 } from "@busbud/core-types/dist/lib/primitives";

import type { CurrencyCode } from "@app/types/currency";
import type {
  ISO8601Datetime,
  NapiAmount,
  NapiV2PurchaseBilledTotal,
  NapiV2PurchaseCharges,
  NapiV2User
} from "@app/types/napi-v2";
import type {
  AncillaryCategory,
  NapiPrice
} from "@app/types/napi-v2-ancillary";
import type { NapiPurchaseCancellationState } from "@app/types/napi-v2-cancellation-types";
import type { NapiV2TicketItem } from "@app/types/napi-v2-tickets-types";

export type PurchasedAncillaryItemStatus =
  | "STARTED"
  | "FAILED"
  | "COMPLETED"
  | "CANCELLED"
  | "CANCELLATION_ERROR";
interface GenericPurchasedAncillaryItem<
  T extends AncillaryCategory,
  V extends {}
> {
  type: "ancillary";
  id: string;
  product_id: string;
  category: T;
  display_price: NapiPrice;
  state: PurchasedAncillaryItemStatus;
  ancillary_details: V;
}
export interface HepstarRefundablePolicy {
  issue_date: string;
  transaction_reference: string;
  policy_number: string;
  policy_rules_url: string;
  policy_schedule_download: string;
  price: number;
  currency: CurrencyCode;
  product_id: string;
}

export type HepstarRefundProtectionPurchasedAncillaryItem =
  GenericPurchasedAncillaryItem<
    "HEPSTAR",
    {
      type: "HEPSTAR_REFUND_PROTECT";
      policies?: HepstarRefundablePolicy[];
    }
  >;

export type ProtectGroupRefundProtectionPurchasedAncillaryItem =
  GenericPurchasedAncillaryItem<
    "PROTECT_GROUP",
    {
      type: "PROTECT_GROUP";
      policies?: HepstarRefundablePolicy[];
    }
  >;

export function isHepstarRefundProtectionPurchasedAncillaryItem(
  item: AncillaryPurchasedItem
): item is HepstarRefundProtectionPurchasedAncillaryItem {
  return (
    item.category === "HEPSTAR" &&
    item.ancillary_details.type === "HEPSTAR_REFUND_PROTECT"
  );
}

export function isProtectGroupRefundProtectionPurchasedAncillaryItem(
  item: AncillaryPurchasedItem
): item is ProtectGroupRefundProtectionPurchasedAncillaryItem {
  return (
    item.category === "PROTECT_GROUP" &&
    item.ancillary_details.type === "PROTECT_GROUP"
  );
}

export type HepstarClimeworksPurchasedAncillaryItem =
  GenericPurchasedAncillaryItem<
    "HEPSTAR",
    {
      type: "HEPSTAR_CLIMEWORKS";
      policies?: HepstarRefundablePolicy[];
    }
  >;
export function isHepstarClimeworksPurchasedAncillaryItem(
  item: AncillaryPurchasedItem
): item is HepstarClimeworksPurchasedAncillaryItem {
  return (
    item.category === "HEPSTAR" &&
    item.ancillary_details.type === "HEPSTAR_CLIMEWORKS"
  );
}

export type CfarRefundProtectionPurchasedAncillaryItem =
  GenericPurchasedAncillaryItem<
    "CFAR",
    {
      type: "CFAR";
      policies: {
        terms_and_conditions_link: string;
        cutoff_to: number;
      };
    }
  >;
export function isCfarRefundProtectionPurchasedAncillaryItem(
  item: AncillaryPurchasedItem
): item is CfarRefundProtectionPurchasedAncillaryItem {
  return item.category === "CFAR";
}

export type ESimPurchasedAncillaryItem = GenericPurchasedAncillaryItem<
  "ESIM",
  {
    type: "ESIM";
  }
>;
export function isESimPurchasedAncillaryItem(
  item: AncillaryPurchasedItem
): item is ESimPurchasedAncillaryItem {
  return item.category === "ESIM";
}

export type AncillaryPurchasedItem =
  | HepstarRefundProtectionPurchasedAncillaryItem
  | HepstarClimeworksPurchasedAncillaryItem
  | ProtectGroupRefundProtectionPurchasedAncillaryItem
  | CfarRefundProtectionPurchasedAncillaryItem
  | ESimPurchasedAncillaryItem;

export type NapiV2PurchasedItem = NapiV2TicketItem | AncillaryPurchasedItem;
export function isAncillaryPurchasedItem(
  item: NapiV2PurchasedItem
): item is AncillaryPurchasedItem {
  return item.type === "ancillary";
}
export function isTicketPurchaseItem(
  item: NapiV2PurchasedItem
): item is NapiV2TicketItem {
  return item.type === "ticket";
}

export interface NapiV2InstallmentsPlan {
  id: string;
  installments: number;
  amount: number;
}

type TicketState = "none" | "pending" | "success";
export interface NapiV2Purchase {
  id: number;
  uuid: string;
  cart_id: string;
  partner?: { id: number };
  state: "in_progress" | "failed" | "delayed" | "booked";
  created_at: ISO8601Datetime;
  completed_at: ISO8601Datetime | null;
  delivered_at: ISO8601Datetime | null;
  tickets_state: TicketState;
  purchaser: NapiV2User;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    locale: string;
    email: string;
    phone_number: string | null;
  };
  trips: Record<string, PollService.API.PublicTrip>;
  items: NapiV2PurchasedItem[];
  charges: NapiV2PurchaseCharges;
  billed_totals: NapiV2PurchaseBilledTotal[];
  cancellation: NapiPurchaseCancellationState | undefined;
  stats: {
    customer_value: NapiPrice;
    customer_value_cad: number;
  };
  ticket_bundle?: {
    pkpasses_url: string | null;
    transit_object_jwt: string | null;
    pdf_url: string | null;
  };
}

export interface NapiV2PurchaseDetails extends NapiV2Purchase {
  stats: {
    customer_value: NapiPrice;
    customer_value_cad: number;
    gmv_value_cad: number;
    partner: {
      customer_value: number;
      gmv: number;
      currency: CurrencyCode;
    };
  };
  purchase_reward?: NapiV2PurchaseReward | null;
}

export interface NapiV2PurchaseReward {
  amount: NapiAmount;
  amount_in_wallet_currency: NapiAmount;
  tickets_count: number;
  visited_countries: CountryCode2[];
  traveled_distance: number;
  purchased_at: string;
  travel_date: string;
  rewarded_at: string | null;
}
